import React from "react";
import {
  ArrowRight,
  Cable,
  Landmark,
  RadioTower,
  ReceiptText,
  ShieldCheck,
} from "lucide-react";
import { Button, ErrorState, Skeleton } from "../../components/ui/primitives";

const moneyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const fractionalMoneyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const quantityFormatter = new Intl.NumberFormat("en-IN");

export function formatCommercialMoney(paise) {
  const numericPaise = Number(paise);
  const formatter = numericPaise % 100 === 0 ? moneyFormatter : fractionalMoneyFormatter;
  return formatter.format(numericPaise / 100);
}

export function formatCommercialMdr(mdr) {
  if (!mdr || typeof mdr !== "object") return "Unavailable";
  if (mdr.kind === "nil") return "NIL";
  if (mdr.kind === "flat") return formatCommercialMoney(mdr.paise);
  if (mdr.kind === "percentage") return `${(Number(mdr.bps) / 100).toFixed(2)}%`;
  if (mdr.kind === "percentage_or_flat") {
    return `${(Number(mdr.bps) / 100).toFixed(2)}% or ${formatCommercialMoney(mdr.paise)}`;
  }
  return "Unavailable";
}

export function formatIntegrationCharge(charge, optional) {
  if (!charge || typeof charge !== "object") return "Unavailable";
  const amount = formatCommercialMoney(charge.paise);
  let basis = "";
  if (charge.kind === "monthly_billed_annually") basis = "/month (billed annually)";
  if (charge.kind === "per_vehicle") basis = " / vehicle";
  return `${amount}${basis}${optional ? " — Optional" : ""}`;
}

export function isCommercialCostSchedule(schedule) {
  return Boolean(
    schedule
    && typeof schedule.revision === "string"
    && /^[0-9a-f]{64}$/.test(schedule.revision)
    && schedule.currency === "INR"
    && Array.isArray(schedule.gateway?.groups)
    && schedule.gateway.groups.length > 0
    && schedule.gateway.groups.every((group) => (
      typeof group?.id === "string"
      && typeof group?.label === "string"
      && Array.isArray(group?.rows)
      && group.rows.length > 0
    ))
    && Array.isArray(schedule.integrations)
    && schedule.integrations.length > 0
    && Array.isArray(schedule.services)
    && schedule.services.length > 0
  );
}

function SectionHeading({ eyebrow, icon: Icon, id, title, description, meta }) {
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,0.72fr)_minmax(18rem,1fr)] lg:items-end">
      <div>
        <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-accent-active">
          <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
          {eyebrow}
        </p>
        <h3
          id={id}
          className="mt-3 text-balance font-heading text-[27px] font-bold leading-[1.12] tracking-[-0.035em] text-ink sm:text-[34px]"
        >
          {title}
        </h3>
      </div>
      <div className="lg:pb-0.5">
        <p className="max-w-2xl text-[14px] leading-6 text-ink-2 sm:text-[15px]">{description}</p>
        {meta ? <p className="mt-2 text-[12px] font-semibold leading-5 text-ink-subtle">{meta}</p> : null}
      </div>
    </div>
  );
}

function GatewayLedger({ group }) {
  return (
    <article
      className="min-w-0 overflow-hidden rounded-2xl border border-line bg-surface shadow-card"
      data-gateway-group={group.id}
    >
      <h4 className="bg-primary px-4 py-3.5 font-heading text-[15px] font-semibold tracking-[-0.01em] text-primary-fg sm:px-5 sm:text-[16px]">
        {group.label}
      </h4>
      <table className="w-full table-fixed border-collapse text-left" aria-label={`${group.label} gateway charges`}>
        <caption className="sr-only">{group.label}: method, MDR and Vidyapeeth360 platform fee</caption>
        <colgroup>
          <col className="w-[48%]" />
          <col className="w-[24%]" />
          <col className="w-[28%]" />
        </colgroup>
        <thead className="bg-muted/70 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-subtle sm:text-[11px]">
          <tr>
            <th scope="col" className="px-3 py-3 sm:px-5">Method</th>
            <th scope="col" className="px-2 py-3 text-center">
              <abbr title="Merchant discount rate" className="no-underline">MDR</abbr>
            </th>
            <th scope="col" className="px-2 py-3 text-center leading-4 sm:px-4">Platform fee</th>
          </tr>
        </thead>
        <tbody>
          {group.rows.map((row) => (
            <tr
              key={row.id}
              className="border-t border-line text-[12.5px] text-ink transition-colors duration-fast hover:bg-muted/45 motion-reduce:transition-none sm:text-[13.5px]"
              data-gateway-row={row.id}
            >
              <th scope="row" className="break-words px-3 py-4 font-semibold leading-5 sm:px-5 sm:py-[18px]">
                {row.method}
              </th>
              <td className="break-words px-2 py-4 text-center font-medium leading-5 text-ink-2 sm:py-[18px]">
                {formatCommercialMdr(row.mdr)}
              </td>
              <td className="px-2 py-4 text-center font-medium leading-5 text-ink-2 sm:px-4 sm:py-[18px]">
                {row.platform_fee_paise === null
                  ? "N/A"
                  : formatCommercialMoney(row.platform_fee_paise)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}

function IntegrationLedger({ rows }) {
  return (
    <div className="mt-7 overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
      <table className="w-full table-fixed border-collapse text-left" aria-label="Integration costs">
        <caption className="sr-only">Integration and setup item with its commercial charge</caption>
        <colgroup>
          <col className="w-[52%]" />
          <col className="w-[48%]" />
        </colgroup>
        <thead className="bg-vidya-950 text-[11px] font-bold uppercase tracking-[0.11em] text-white sm:text-[12px]">
          <tr>
            <th scope="col" className="px-4 py-4 sm:px-6">Integration</th>
            <th scope="col" className="px-4 py-4 sm:px-6">Cost and billing basis</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-t border-line text-[13px] transition-colors duration-fast hover:bg-muted/45 motion-reduce:transition-none sm:text-[14px]"
              data-integration-row={row.id}
            >
              <th scope="row" className="break-words px-4 py-4 font-semibold leading-5 text-ink sm:px-6 sm:py-5">
                {row.integration}
              </th>
              <td className="break-words px-4 py-4 font-medium leading-5 text-ink-2 sm:px-6 sm:py-5">
                <span>{formatIntegrationCharge(row.charge, row.optional)}</span>
                {row.condition ? (
                  <span className="mt-1 block text-[12px] font-normal leading-5 text-ink-subtle">
                    {row.condition}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceLedger({ rows }) {
  return (
    <div className="mt-7 overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
      <table className="w-full table-fixed border-collapse text-left" aria-label="Usage-based service costs">
        <caption className="sr-only">Service, cost per unit and bundle example</caption>
        <colgroup>
          <col className="w-[34%]" />
          <col className="w-[29%]" />
          <col className="w-[37%]" />
        </colgroup>
        <thead className="bg-vidya-950 text-[10px] font-bold uppercase tracking-[0.1em] text-white sm:text-[12px]">
          <tr>
            <th scope="col" className="px-3 py-4 sm:px-6">Service</th>
            <th scope="col" className="px-2 py-4 sm:px-6">Cost per unit</th>
            <th scope="col" className="px-2 py-4 sm:px-6">Bundle example</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-t border-line text-[12.5px] transition-colors duration-fast hover:bg-muted/45 motion-reduce:transition-none sm:text-[14px]"
              data-service-row={row.id}
            >
              <th scope="row" className="break-words px-3 py-4 font-semibold leading-5 text-ink sm:px-6 sm:py-5">
                {row.service}
              </th>
              <td className="break-words px-2 py-4 font-medium leading-5 text-ink-2 sm:px-6 sm:py-5">
                {formatCommercialMoney(row.unit_rate.paise)} / {row.unit_rate.unit_label}
              </td>
              <td className="break-words px-2 py-4 font-medium leading-5 text-ink-2 sm:px-6 sm:py-5">
                {formatCommercialMoney(row.bundle.total_paise)} for{" "}
                {quantityFormatter.format(row.bundle.quantity)} {row.bundle.unit_label}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PricingCostLoading() {
  return (
    <section className="border-y border-line bg-muted/55" aria-labelledby="commercial-costs-loading-heading" data-testid="pricing-commercial-costs">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
        <h2 id="commercial-costs-loading-heading" className="sr-only">Loading commercial costs</h2>
        <div role="status" aria-busy="true" aria-label="Loading gateway, integration and service costs" className="grid gap-5 sm:grid-cols-2">
          <Skeleton className="h-56 rounded-2xl" />
          <Skeleton className="h-56 rounded-2xl" />
          <Skeleton className="h-56 rounded-2xl sm:col-span-2" />
        </div>
      </div>
    </section>
  );
}

export default function PricingCostDisclosure({ schedule, loading, error, onRetry, onRequestReview }) {
  if (loading) return <PricingCostLoading />;

  if (error || !isCommercialCostSchedule(schedule)) {
    return (
      <section className="border-y border-line bg-muted/55" aria-labelledby="commercial-costs-error-heading" data-testid="pricing-commercial-costs">
        <div className="mx-auto max-w-2xl px-5 py-14 sm:py-16">
          <h2 id="commercial-costs-error-heading" className="sr-only">Commercial costs unavailable</h2>
          <ErrorState
            title="Commercial schedule is unavailable"
            desc="No local fallback rates are shown. Reload the governed pricing response before making a purchasing decision."
            onRetry={onRetry}
            retryLabel="Reload commercial schedule"
          />
        </div>
      </section>
    );
  }

  return (
    <section
      id="commercial-costs"
      aria-labelledby="commercial-costs-heading"
      className="scroll-mt-24 border-y border-line bg-muted/55"
      data-testid="pricing-commercial-costs"
      data-schedule-revision={schedule.revision}
    >
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16 lg:py-20">
        <header className="grid gap-7 lg:grid-cols-[minmax(0,0.84fr)_minmax(21rem,1.16fr)] lg:items-end lg:gap-16">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-accent-active">
              <ReceiptText className="h-4 w-4" aria-hidden="true" />
              Commercial schedule
            </p>
            <h2
              id="commercial-costs-heading"
              className="mt-4 max-w-xl text-balance font-heading text-[30px] font-bold leading-[1.1] tracking-[-0.04em] text-ink sm:text-[40px]"
            >
              Every charge, separated by what creates it.
            </h2>
          </div>
          <div>
            <p className="max-w-2xl text-[15px] leading-7 text-ink-2">
              These charges sit outside the base subscription. Payment-method MDR and the
              Vidyapeeth360 platform fee are listed separately, followed by optional integrations
              and metered communication services.
            </p>
            <div className="mt-4 flex items-start gap-3 border-l-2 border-primary pl-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <p className="text-[12.5px] leading-5 text-ink-subtle">
                Your Order Form confirms plan eligibility, approved scope and tax treatment.
                Settlement evidence remains in the connected gateway and bank records.
              </p>
            </div>
          </div>
        </header>

        <nav aria-label="Commercial cost sections" className="mt-9 flex flex-wrap gap-2 border-b border-line pb-5">
          {[
            ["#payment-gateway-charges", "Gateway charges"],
            ["#integration-costs", "Integration costs"],
            ["#service-costs", "Service costs"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="rounded-full border border-line bg-surface px-3.5 py-2 text-[12px] font-semibold text-ink-2 transition-colors duration-fast hover:border-primary/40 hover:text-accent-active focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              {label}
            </a>
          ))}
        </nav>

        <article
          id="payment-gateway-charges"
          aria-labelledby="payment-gateway-heading"
          className="scroll-mt-28 pt-12 sm:pt-14"
          data-cost-category="gateway"
        >
          <SectionHeading
            eyebrow="Online collections"
            icon={Landmark}
            id="payment-gateway-heading"
            title="Charges on an online collection"
            description="The applicable MDR depends on the payment method. The platform fee is shown in its own column so the two charges remain unambiguous."
            meta={schedule.gateway.availability_label}
          />
          <div className="mt-7 grid gap-4 lg:grid-cols-2 lg:gap-5">
            {schedule.gateway.groups.map((group) => <GatewayLedger key={group.id} group={group} />)}
          </div>
        </article>

        <article
          id="integration-costs"
          aria-labelledby="integration-costs-heading"
          className="scroll-mt-28 border-t border-line pt-12 sm:mt-14 sm:pt-14"
          data-cost-category="integration"
        >
          <SectionHeading
            eyebrow="Implementation"
            icon={Cable}
            id="integration-costs-heading"
            title="One-time integration & setup costs"
            description="One-time and device-linked work is itemised before activation. Biometric server hosting is the recurring exception and is labelled with its annual billing basis."
            meta="Optional items are charged only when included in the approved school scope."
          />
          <IntegrationLedger rows={schedule.integrations} />
        </article>

        <article
          id="service-costs"
          aria-labelledby="service-costs-heading"
          className="scroll-mt-28 border-t border-line pt-12 sm:mt-14 sm:pt-14"
          data-cost-category="services"
        >
          <SectionHeading
            eyebrow="Metered communication"
            icon={RadioTower}
            id="service-costs-heading"
            title="Service costs"
            description="Usage is charged by the channel unit. Bundle examples show the arithmetic for 10,000 units; they are examples, not minimum purchase commitments."
            meta="Actual usage and applicable channel records determine the billed quantity."
          />
          <ServiceLedger rows={schedule.services} />
        </article>

        <div className="mt-12 flex flex-col gap-5 rounded-2xl bg-vidya-950 px-5 py-6 text-white sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="max-w-xl">
            <p className="font-heading text-[16px] font-semibold text-white">Review one complete school-specific cost sheet.</p>
            <p className="mt-1.5 text-[12.5px] leading-5 text-white/70">
              Confirm the subscription, eligible payment methods, selected integrations, expected usage and tax treatment together.
            </p>
          </div>
          <Button
            type="button"
            onClick={onRequestReview}
            className="!min-h-11 shrink-0 !bg-white !px-5 !text-vidya-950 hover:!bg-white/90 focus-visible:!ring-white/75 focus-visible:!ring-offset-vidya-950"
            data-testid="pricing-cost-review"
          >
            Review costs for my school <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </section>
  );
}
